import { useMemo, useState } from "react";
import { FiClipboard } from "react-icons/fi";
import { handleClientInsert } from "../services/api";
import useAppDataStore from "../../states/useAppDataStore";
import { Input, Modal } from "@iq/models";

const STATUS_OPTIONS = ["Active", "Pending", "Inactive"];
const requiredMarkStyle = { color: "var(--color-danger)", marginLeft: "2px" };

function InsertClientModal({ onClose, onInserted }) {
    const organizationOptions = useAppDataStore((state) => state.organizationOptions);
    const addLocalOrganizationOption = useAppDataStore((state) => state.addLocalOrganizationOption);

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        organization: "",
        organizationId: "",
        status: "Pending",
    });
    const [organizationQuery, setOrganizationQuery] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const normalizedQuery = organizationQuery.trim().toLowerCase();
    const filteredOrganizations = useMemo(
        () => organizationOptions.filter((org) => org.name.toLowerCase().includes(normalizedQuery)),
        [organizationOptions, normalizedQuery]
    );

    const canCreateNewOrg = Boolean(normalizedQuery) &&
        !organizationOptions.some((org) => org.name.toLowerCase() === normalizedQuery);

    const isFormValid = Boolean(
        form.organization.trim() &&
        form.name.trim() &&
        form.email.trim() &&
        form.phone.trim() &&
        form.status
    );

    function handleFieldChange(event) {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    function handleOrganizationInputChange(event) {
        const value = event.target.value;
        setOrganizationQuery(value);
        setForm((prev) => ({ ...prev, organization: value, organizationId: "" }));
    }

    function selectOrganization(org) {
        setOrganizationQuery(org.name);
        setForm((prev) => ({
            ...prev,
            organization: org.name,
            organizationId: org.id != null ? String(org.id) : "",
        }));
    }

    function createOrganizationFromQuery() {
        const value = organizationQuery.trim();
        if (!value) {
            return;
        }

        setOrganizationQuery(value);
        setForm((prev) => ({ ...prev, organization: value, organizationId: "" }));

        addLocalOrganizationOption(value);
    }

    function selectStatus(status) {
        setForm((prev) => ({ ...prev, status }));
    }

    async function handleSubmit(event) {
        event?.preventDefault?.();
        if (!isFormValid) {
            return;
        }
        setSubmitError("");
        setSubmitting(true);

        try {
            await handleClientInsert({
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                organization: form.organization.trim(),
                organization_id: form.organizationId ? Number(form.organizationId) : null,
                status: form.status,
            });

            await onInserted?.();
            onClose?.();
        } catch (error) {
            setSubmitError(error?.message || "Failed to insert client");
        } finally {
            setSubmitting(false);
        }
    }

    const labelStyle = {
        fontSize: "var(--font-size-xs)",
        fontWeight: 600,
        color: "var(--color-fg-muted)",
        marginBottom: "4px",
        display: "block",
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="Add Client"
            icon={<FiClipboard size={32} />}
            shrink
            errorMessage={submitError}
            actionButtons={[
                {
                    key: "submit",
                    text: submitting ? "Saving..." : "Add +",
                    color: "accent",
                    disabled: submitting || !isFormValid,
                    onClick: handleSubmit,
                },
            ]}
        >
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", width: "100%" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--space-3)" }}>
                    <div className="hybrid-account-field">
                        <label style={labelStyle} htmlFor="organization">
                            Company Name<span style={requiredMarkStyle}>*</span>
                        </label>
                        <Input
                            id="organization"
                            name="organization"
                            type="dropdown"
                            colorScheme="dark"
                            value={organizationQuery}
                            placeholder="e.g. Acme Inc."
                            onChange={handleOrganizationInputChange}
                            ariaLabel="Organization"
                        >
                            {({ closeMenu }) => (
                                <>
                                    {filteredOrganizations.map((org) => (
                                        <button
                                            type="button"
                                            key={`${org.id ?? "new"}-${org.name}`}
                                            className="dropdown-menu-item"
                                            onClick={() => {
                                                selectOrganization(org);
                                                closeMenu();
                                            }}
                                        >
                                            {org.name}
                                        </button>
                                    ))}

                                    {canCreateNewOrg ? (
                                        <button
                                            type="button"
                                            className="dropdown-menu-item create-new"
                                            onClick={() => {
                                                createOrganizationFromQuery();
                                                closeMenu();
                                            }}
                                        >
                                            Create new organization: "{organizationQuery.trim()}"
                                        </button>
                                    ) : null}
                                </>
                            )}
                        </Input>
                    </div>

                    <div>
                        <label style={labelStyle} htmlFor="name">
                            Name<span style={requiredMarkStyle}>*</span>
                        </label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            colorScheme="dark"
                            value={form.name}
                            onChange={handleFieldChange}
                            placeholder="e.g. John Smith"
                        />
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--space-3)" }}>
                    <div>
                        <label style={labelStyle} htmlFor="email">
                            Email<span style={requiredMarkStyle}>*</span>
                        </label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            colorScheme="dark"
                            value={form.email}
                            onChange={handleFieldChange}
                            placeholder="e.g. john.smith@acmecorp.com"
                        />
                    </div>

                    <div>
                        <label style={labelStyle} htmlFor="phone">
                            Phone Number<span style={requiredMarkStyle}>*</span>
                        </label>
                        <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            colorScheme="dark"
                            value={form.phone}
                            onChange={handleFieldChange}
                            placeholder="e.g. +12345678900"
                        />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>
                        Status<span style={requiredMarkStyle}>*</span>
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--space-2)" }}>
                        {STATUS_OPTIONS.map((option) => {
                            const isSelected = form.status === option;
                            return (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => selectStatus(option)}
                                    style={{
                                        padding: "10px 12px",
                                        borderRadius: "var(--radius-sm)",
                                        border: isSelected ? "1px solid var(--color-highlight)" : "1px solid var(--color-surface-soft)",
                                        background: "rgba(239, 255, 246, 0.05)",
                                        color: isSelected ? "var(--color-highlight)" : "var(--color-fg-default)",
                                        fontSize: "var(--font-size-sm)",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                    }}
                                >
                                    {option}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </form>
        </Modal>
    );
}

export default InsertClientModal;
